type VivusTarget = string | Element;

interface VivusOptions {
    duration?: number;
    forceRender?: boolean;
    pathTimingFunction?: string;
    animTimingFunction?: string;
    reverseStack?: boolean;
    selfDestroy?: boolean;
    type?: string;
}

interface VivusPathState {
    element: SVGGeometryElement;
    length: number;
}

export default class Vivus {
    public static readonly EASE_OUT = 'cubic-bezier(0.16, 1, 0.3, 1)';

    private readonly paths: VivusPathState[];
    private readonly duration: number;
    private readonly timing: string;
    private readonly selfDestroy: boolean;

    constructor(target: VivusTarget, options: VivusOptions = {}) {
        const svg = resolveSvgElement(target);
        if (!svg) {
            return;
        }

        this.duration = Math.max(120, options.duration ?? 800);
        this.timing = options.pathTimingFunction || options.animTimingFunction || Vivus.EASE_OUT;
        this.selfDestroy = options.selfDestroy !== false;
        this.paths = collectGeometryPaths(svg);

        this.preparePaths();
        requestAnimationFrame(() => {
            this.play();
        });
    }

    private preparePaths(): void {
        this.paths.forEach(({ element, length }) => {
            element.style.strokeDasharray = `${length}`;
            element.style.strokeDashoffset = `${length}`;
            element.style.willChange = 'stroke-dashoffset';
            element.getBoundingClientRect();
        });
    }

    private play(): void {
        this.paths.forEach(({ element }) => {
            element.style.transition = `stroke-dashoffset ${this.duration}ms ${this.timing}`;
            element.style.strokeDashoffset = '0';
        });

        if (!this.selfDestroy) {
            return;
        }

        window.setTimeout(() => {
            this.paths.forEach(({ element }) => {
                element.style.removeProperty('stroke-dasharray');
                element.style.removeProperty('stroke-dashoffset');
                element.style.removeProperty('transition');
                element.style.removeProperty('will-change');
            });
        }, this.duration + 32);
    }
}

function resolveSvgElement(target: VivusTarget): SVGSVGElement | null {
    if (typeof document === 'undefined') {
        return null;
    }

    const element = typeof target === 'string'
        ? document.querySelector(target)
        : target;

    if (element instanceof SVGSVGElement) {
        return element;
    }

    return element?.querySelector?.('svg') ?? null;
}

function collectGeometryPaths(svg: SVGSVGElement): VivusPathState[] {
    const candidates = svg.querySelectorAll<SVGGeometryElement>('path, line, polyline, polygon, rect, circle, ellipse');
    return Array.from(candidates)
        .map((element) => ({
            element,
            length: resolveLength(element)
        }))
        .filter((entry) => entry.length > 0);
}

function resolveLength(element: SVGGeometryElement): number {
    try {
        return Math.max(1, element.getTotalLength());
    } catch {
        const bbox = element.getBBox?.();
        if (!bbox) {
            return 1;
        }

        return Math.max(1, (bbox.width + bbox.height) * 2);
    }
}
